/**
 * What the builder holds while a leg is being typed — ROADMAP 5.23.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A DRAFT IS STRINGS, AND THAT IS NOT LAZINESS
 *
 * Every numeric field here is a string, because a half-typed number is a
 * real state a form must survive. "−" on its way to "−2", "0." on its way to
 * "0.15", and an empty box a trader has cleared before retyping are all
 * things `number` cannot hold: coerce early and the cursor jumps, the minus
 * sign vanishes as it is typed, and clearing a field silently writes 0.
 *
 * So the draft is text, the conversion happens once on save, and this file
 * is the only place the two meet. It is pure — no React, no fetch — so the
 * conversion is tested without a DOM, which is the whole reason it lives in
 * `utils/` rather than inside the component.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  CRITERIA_LIMITS,
  stepsForMoneyness,
  moneynessOf,
  strikeCriteriaProblem,
  type ExpiryKind,
  type LegRole,
  type OptionType,
  type Side,
  type StrikeCriteria,
} from '@apna/shared'
import type { StrategyLegDto } from '@/types/strategy'

/**
 * How the FORM names a way of picking a strike.
 *
 * Seven here against five stored, because ATM, ITM and OTM are one stored
 * variant: they all land some number of steps from the money, and the
 * translation is `stepsForMoneyness`. Splitting them in the form and not in
 * the database is the right way round — a trader thinks in moneyness, and
 * the resolver should not have to.
 */
export const STRIKE_MODES = [
  'ATM',
  'OTM',
  'ITM',
  'CLOSEST_PREMIUM',
  'PREMIUM_RANGE',
  'CLOSEST_DELTA',
  'DELTA_RANGE',
] as const

export type StrikeMode = (typeof STRIKE_MODES)[number]

export const STRIKE_MODE_LABEL: Record<StrikeMode, string> = {
  ATM: 'At the money',
  OTM: 'Out of the money',
  ITM: 'In the money',
  CLOSEST_PREMIUM: 'Closest premium',
  PREMIUM_RANGE: 'Premium range',
  CLOSEST_DELTA: 'Closest delta',
  DELTA_RANGE: 'Delta range',
}

/** How many numeric boxes a mode shows, and what goes in them. */
export interface ModeShape {
  readonly fields: 0 | 1 | 2
  readonly unit: string
  readonly placeholder: readonly [string, string]
  readonly step: string
}

export const MODE_SHAPE: Record<StrikeMode, ModeShape> = {
  ATM: { fields: 0, unit: '', placeholder: ['', ''], step: '1' },
  OTM: { fields: 1, unit: 'strikes', placeholder: ['2', ''], step: '1' },
  ITM: { fields: 1, unit: 'strikes', placeholder: ['2', ''], step: '1' },
  CLOSEST_PREMIUM: { fields: 1, unit: '₹', placeholder: ['120', ''], step: '0.05' },
  PREMIUM_RANGE: { fields: 2, unit: '₹', placeholder: ['8', '12'], step: '0.05' },
  CLOSEST_DELTA: { fields: 1, unit: 'Δ', placeholder: ['0.20', ''], step: '0.01' },
  DELTA_RANGE: { fields: 2, unit: 'Δ', placeholder: ['0.10', '0.25'], step: '0.01' },
}

/**
 * What a box will ACCEPT as it is typed — ROADMAP 5.23.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * REFUSED AT THE KEYSTROKE, NOT AT THE SAVE
 *
 * Every rule below is also checked on save, so this is not the thing that
 * keeps bad data out. It is the thing that keeps a trader from typing
 * something for ten seconds and being told afterwards.
 *
 * "OTM −10" is the case that made it necessary. It reads perfectly well, it
 * is accepted by a `type="number"` box, and it is nonsense: out of the money
 * is already a direction, so the minus sign asks for the opposite of the
 * word next to it. The save would have caught it — and by then the trader
 * has written a leg they thought was fine.
 *
 * A REJECTED KEYSTROKE LEAVES THE FIELD ALONE rather than rewriting it.
 * Silently turning "−10" into "10" is worse than refusing: the trader meant
 * something by the sign, and a field that edits itself under the cursor is
 * one nobody trusts.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export type NumericKind = 'STEPS' | 'PREMIUM' | 'DELTA' | 'COUNT'

/** Upper bound and decimal places per kind. No kind admits a sign. */
const NUMERIC_RULES: Record<NumericKind, { max: number; decimals: number }> = {
  /** Distance from the money, in whole strikes. */
  STEPS: { max: CRITERIA_LIMITS.maxSteps, decimals: 0 },
  /** Rupees. Two places, because a strike can genuinely be worth ₹0.05. */
  PREMIUM: { max: CRITERIA_LIMITS.maxPremium, decimals: 2 },
  /** A magnitude between 0 and 1 — see `CLOSEST_DELTA`. */
  DELTA: { max: CRITERIA_LIMITS.maxDelta, decimals: 2 },
  /** Lots, and a leg's size relative to the others. */
  COUNT: { max: CRITERIA_LIMITS.maxRatio, decimals: 0 },
}

/** Which rules the value boxes of a mode follow. */
export function numericKindOf(mode: StrikeMode): NumericKind {
  switch (mode) {
    case 'ATM':
    case 'OTM':
    case 'ITM':
      return 'STEPS'
    case 'CLOSEST_PREMIUM':
    case 'PREMIUM_RANGE':
      return 'PREMIUM'
    case 'CLOSEST_DELTA':
    case 'DELTA_RANGE':
      return 'DELTA'
  }
}

/**
 * The value a box should hold after this keystroke.
 *
 * Returns `previous` to refuse the edit. Empty is always allowed — a trader
 * clearing a field before retyping must not be fought — and so is a trailing
 * decimal point, which is a real waypoint between "0" and "0.2".
 */
export function acceptNumeric(kind: NumericKind, raw: string, previous: string): string {
  const { max, decimals } = NUMERIC_RULES[kind]
  const next = raw.trim()
  if (next === '') return ''

  /*
   * NO SIGN, NO EXPONENT, NO SPACES, and for a whole-number field no point
   * either. Checked by whitelist rather than by stripping, so a pasted
   * "−10" or "1e3" is refused outright instead of silently becoming
   * something else.
   */
  const pattern = decimals === 0 ? /^\d+$/ : new RegExp(`^\\d*\\.?\\d{0,${decimals}}$`)
  if (!pattern.test(next)) return previous

  // "." and "0." are on the way to a number, not numbers. Let them stand.
  if (next === '.' || next.endsWith('.')) return next

  const value = Number(next)
  if (!Number.isFinite(value) || value > max) return previous
  return next
}

/** One leg mid-edit. `id` absent means it does not exist in the database yet. */
export interface LegDraft {
  /** Stable across re-renders and reorders. NOT the database id — see `newLeg`. */
  key: string
  id?: string
  side: Side
  optionType: OptionType
  /** Empty string is "the strategy's expiry", which is not the same as WEEKLY. */
  expiryKind: ExpiryKind | ''
  mode: StrikeMode
  /** The first box: steps, premium, delta, or the low end of a band. */
  a: string
  /** The second box: the high end of a band. Unused by the single-value modes. */
  b: string
  /**
   * THIS LEG'S SIZE IN LOTS, not the stored ratio.
   *
   * The database keeps one `defaultLots` for the strategy and a RELATIVE
   * `ratio` per leg, because that is what makes a 1:2 ratio spread scale
   * correctly when a trader fires it at a different size. A form that asked
   * for "ratio" would be asking a trader to do that division in their head.
   *
   * So the form asks for lots and `decomposeLots` does the arithmetic. See
   * it for why the magnitude cannot simply live in `ratio`.
   */
  lots: string
  role: LegRole | ''
}

let seq = 0
const nextKey = (): string => `draft-${(seq += 1)}`

/** A fresh leg, copying the side and type of the one above so a condor is four edits. */
export function newLeg(after?: LegDraft): LegDraft {
  return {
    key: nextKey(),
    side: after?.side ?? 'SELL',
    optionType: after?.optionType === 'CE' ? 'PE' : 'CE',
    expiryKind: after?.expiryKind ?? '',
    mode: 'ATM',
    a: '',
    b: '',
    lots: after?.lots ?? '1',
    role: '',
  }
}

/**
 * A duplicate of one leg, ready to be edited into its partner.
 *
 * The fastest way to a four-legger: copy the sold call, flip it to a put,
 * done. Everything is carried EXCEPT the identity — a copy has no `id`, so
 * it is created rather than overwriting the leg it came from, and a fresh
 * `key` so React treats the two rows as different.
 */
export function copyLeg(d: LegDraft): LegDraft {
  const { id: _id, key: _key, ...rest } = d
  return { ...rest, key: nextKey() }
}

export function draftFromLeg(leg: StrategyLegDto, defaultLots: number): LegDraft {
  const c = leg.strikeCriteria
  const base = {
    key: leg.id,
    id: leg.id,
    side: leg.side,
    optionType: leg.optionType,
    expiryKind: leg.expiryKind ?? ('' as const),
    lots: String(Math.max(1, defaultLots) * leg.ratio),
    role: leg.role ?? ('' as const),
  }

  /*
   * An unreadable rule opens as ATM so the trader can repair it — but it is
   * never SAVED that way unless they press Save, which is the difference
   * between offering a fix and silently applying one.
   */
  if (!c) return { ...base, mode: 'ATM', a: '', b: '' }

  switch (c.kind) {
    case 'ATM_OFFSET': {
      const { moneyness, n } = moneynessOf(c.steps, leg.optionType)
      return { ...base, mode: moneyness, a: moneyness === 'ATM' ? '' : String(n), b: '' }
    }
    case 'CLOSEST_PREMIUM':
      return { ...base, mode: 'CLOSEST_PREMIUM', a: String(c.premium), b: '' }
    case 'PREMIUM_RANGE':
      return { ...base, mode: 'PREMIUM_RANGE', a: String(c.min), b: String(c.max) }
    case 'CLOSEST_DELTA':
      return { ...base, mode: 'CLOSEST_DELTA', a: String(c.delta), b: '' }
    case 'DELTA_RANGE':
      return { ...base, mode: 'DELTA_RANGE', a: String(c.min), b: String(c.max) }
  }
}

/** A leg as the API takes it. Mirrors `legSpecSchema` on the server. */
export interface LegWire {
  id?: string
  side: Side
  optionType: OptionType
  strikeCriteria: StrikeCriteria
  ratio: number
  role: LegRole | null
  expiryKind: ExpiryKind | null
}

export type DraftResult<T> = { ok: true; value: T } | { ok: false; why: string }

/** The number in a box, or null when it is blank or not a number. */
const num = (raw: string): number | null => {
  const t = raw.trim()
  if (t === '') return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

/**
 * The stored criterion a draft describes.
 *
 * Bounds are NOT re-stated here. `strikeCriteriaProblem` is the same
 * function the API runs, so the form refuses exactly what the server would
 * and the two cannot drift apart — this file only has to decide which
 * variant is being built and whether the boxes were filled in.
 */
export function criteriaOf(d: LegDraft): DraftResult<StrikeCriteria> {
  const built = buildCriteria(d)
  if (!built.ok) return built
  const problem = strikeCriteriaProblem(built.value)
  return problem ? { ok: false, why: problem } : built
}

function buildCriteria(d: LegDraft): DraftResult<StrikeCriteria> {
  const a = num(d.a)
  const b = num(d.b)

  switch (d.mode) {
    case 'ATM':
      return { ok: true, value: { kind: 'ATM_OFFSET', steps: 0 } }

    case 'OTM':
    case 'ITM': {
      if (a === null) return { ok: false, why: 'Say how many strikes from the money.' }
      if (!Number.isInteger(a) || a < 1) {
        return { ok: false, why: 'Distance from the money is a whole number of strikes.' }
      }
      return {
        ok: true,
        value: { kind: 'ATM_OFFSET', steps: stepsForMoneyness(d.mode, a, d.optionType) },
      }
    }

    case 'CLOSEST_PREMIUM':
      if (a === null) return { ok: false, why: 'Say which premium to aim for.' }
      return { ok: true, value: { kind: 'CLOSEST_PREMIUM', premium: a } }

    case 'PREMIUM_RANGE':
      if (a === null || b === null) return { ok: false, why: 'A premium range needs both ends.' }
      return { ok: true, value: { kind: 'PREMIUM_RANGE', min: a, max: b } }

    case 'CLOSEST_DELTA':
      if (a === null) return { ok: false, why: 'Say which delta to aim for.' }
      return { ok: true, value: { kind: 'CLOSEST_DELTA', delta: a } }

    case 'DELTA_RANGE':
      if (a === null || b === null) return { ok: false, why: 'A delta range needs both ends.' }
      return { ok: true, value: { kind: 'DELTA_RANGE', min: a, max: b } }
  }
}

/** Which leg a complaint belongs to, so the form can point at the row. */
export interface LegProblem {
  readonly at: number
  readonly why: string
}

/**
 * Every draft as the API wants it, or the FIRST leg that is not ready.
 *
 * First rather than all: the rows are edited one at a time and a list of
 * seven complaints under the footer is read by nobody. The row itself is
 * marked, so the one being pointed at is visible without reading the message.
 */
/**
 * Per-leg lots, split into the strategy size and each leg's RELATIVE ratio.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY NOT JUST PUT THE LOTS IN `ratio` AND FIX `defaultLots` AT 1?
 *
 * Two reasons, and the second is the one that bites.
 *
 * `ratio` means something: it is the leg's size RELATIVE to the others, and
 * it is what makes a 1:2 ratio spread stay 1:2 when a trader fires it at
 * four times the size. Storing 20 and 40 there would still scale correctly,
 * but the column would have stopped meaning what it says.
 *
 * And `ratio` is capped at 100 while `defaultLots` runs to millions, because
 * one bounds a shape and the other bounds an order. A trader asking for 500
 * lots a leg would be refused by a cap that exists for a different purpose.
 *
 * So the greatest common divisor carries the magnitude and the quotients
 * carry the shape: [20, 20] becomes 20 × [1, 1], and [10, 20] becomes
 * 10 × [1, 2]. Both round-trip to the lots that were typed.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function decomposeLots(
  perLeg: readonly number[],
): DraftResult<{ defaultLots: number; ratios: number[] }> {
  if (perLeg.length === 0) return { ok: false, why: 'A strategy needs at least one leg.' }

  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b))
  const defaultLots = perLeg.reduce((g, n) => gcd(g, n))
  const ratios = perLeg.map((n) => n / defaultLots)

  const widest = Math.max(...ratios)
  if (widest > CRITERIA_LIMITS.maxRatio) {
    /*
     * Reachable only when two legs are coprime and far apart — 1 and 101,
     * say. The shape, not the size, is what fails: 101 times the smallest
     * leg is not a spread anyone trades, and the cap is on the shape.
     */
    return {
      ok: false,
      why: `Leg sizes are too far apart — one leg is ${widest}× another.`,
    }
  }

  return { ok: true, value: { defaultLots, ratios } }
}

export function legsOf(
  drafts: readonly LegDraft[],
): (DraftResult<LegWire[]> & { at?: number; defaultLots?: number }) {
  const criteria: StrikeCriteria[] = []
  const lots: number[] = []

  for (const [i, d] of drafts.entries()) {
    const n = num(d.lots)
    if (n === null || !Number.isInteger(n) || n < 1) {
      return { ok: false, why: 'Lots must be a whole number of 1 or more.', at: i }
    }
    lots.push(n)

    const c = criteriaOf(d)
    if (!c.ok) return { ok: false, why: c.why, at: i }
    criteria.push(c.value)
  }

  const split = decomposeLots(lots)
  if (!split.ok) return { ok: false, why: split.why }

  const out = drafts.map((d, i) => ({
    ...(d.id === undefined ? {} : { id: d.id }),
    side: d.side,
    optionType: d.optionType,
    strikeCriteria: criteria[i]!,
    ratio: split.value.ratios[i]!,
    role: d.role === '' ? null : d.role,
    expiryKind: d.expiryKind === '' ? null : d.expiryKind,
  }))

  return { ok: true, value: out, defaultLots: split.value.defaultLots }
}

/**
 * Two legs that would place the same order twice.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE EXPIRY IS PART OF A LEG'S IDENTITY
 *
 * A calendar spread is two legs alike in side, type and strike, differing
 * only in the week — so comparing without the expiry would call the one
 * structure that needs duplicates a duplicate. Comparing WITH it still
 * catches the real copy-paste slip, which doubles the size the trader asked
 * for while looking like two legs.
 *
 * A duplicate is not refused — the resolver already merges or refuses legs
 * landing on one contract — but the form says so, because at the moment of
 * writing it the trader can still tell which of the two they meant.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function duplicateLegs(drafts: readonly LegDraft[]): readonly number[] {
  const seen = new Map<string, number>()
  const dupes: number[] = []
  for (const [i, d] of drafts.entries()) {
    const id = [d.side, d.optionType, d.expiryKind, d.mode, d.a.trim(), d.b.trim()].join('|')
    if (seen.has(id)) dupes.push(i)
    else seen.set(id, i)
  }
  return dupes
}

/** Does any leg pick its strike by delta? Decides whether the form warns. */
export function usesDelta(drafts: readonly LegDraft[]): boolean {
  return drafts.some((d) => d.mode === 'CLOSEST_DELTA' || d.mode === 'DELTA_RANGE')
}

export const EXPIRY_CHOICE_LABEL: Record<string, string> = {
  '': 'Strategy',
  WEEKLY: 'This week',
  NEXT_WEEKLY: 'Next week',
  MONTHLY: 'This month',
  NEXT_MONTHLY: 'Next month',
}
